<template>
    <div class="mt-8 mx-4 md:mx-0">
        <h1>Submissions</h1>

        <div v-if="selectedSubmissions.length > 0" class="bg-red/10 p-2 pl-4 rounded-full mt-4 mb-2 flex justify-between items-center">
            <div>
                <span class="font-semibold">{{ selectedSubmissions.length }} {{ selectedSubmissions.length === 1 ? 'application' : 'applications' }} selected</span>
            </div>
            <ButtonComponent
                label="Delete applications"
                @click="showDeleteConfirmation"
                color="red"
            />
        </div>

        <!-- Delete Confirmation Modal -->
        <div v-if="showDeleteModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                <h3 class="text-lg font-semibold mb-4">Confirm Deletion</h3>
                <p class="mb-6">Are you sure you want to delete {{ selectedSubmissions.length }} {{ selectedSubmissions.length === 1 ? 'application' : 'applications' }}? This action cannot be undone.</p>
                <div class="flex justify-end gap-4">
                    <ButtonComponent
                        label="Cancel"
                        @click="showDeleteModal = false"
                        color="black"
                    />
                    <ButtonComponent
                        label="Delete"
                        @click="handleBulkDelete"
                        color="red"
                    />
                </div>
            </div>
        </div>

        <div class="grid gap-y-2 md:flex w-full pt-4 items-top">
            <div class="md:w-1/3 md:pr-8">
                <SearchComponent
                    v-model="searchKeyword"
                    class="w-full"
                    placeholder="Search"
                    :disabled="false"
                    @change="handleSearchSubmissions()"
                />
            </div>
            <div class="md:w-1/3">
                <div class="flex flex-wrap md:flex-nowrap items-center w-full">
                    <div class="w-full md:w-auto mr-3">Filters:</div>
                    <div class="mr-3 grow">
                        <DropdownComponent
                            v-model="selectedSubmissionStatusFilter"
                            rules="optional"
                            placeholder="Submission Status"
                            :options="this.submissionStatuses"
                            @change="handleSearchSubmissions()"
                        />
                    </div>
                    <div class="grow">
                        <DropdownComponent
                            v-model="selectedApplicationStatusFilter"
                            rules="optional"
                            placeholder="Application Status"
                            :options="this.applicationStatuses"
                            @change="handleSearchSubmissions()"
                        />
                    </div>
                </div>
            </div>
            <div class="md:w-1/3">
                <div class="flex justify-between md:justify-end">
                    <ButtonComponent label="Export In Progress" :isLoading="!!isLoadingInProgressCsvExport"
                                     @click="doDownloadInProgressApplicationsCsv" />
                    <ButtonComponent class="ml-4" label="Export Submissions" :isLoading="!!isLoadingCsvExport"
                                     @click="doDownloadSubmittedApplicationsCsv" />
                </div>
            </div>
        </div>

        <table class="data-table-component mt-12 w-full hidden md:table"
               :class="isLoadingSubmissions === true ? 'opacity-40' : ''">
            <tr>
                <td>
                    <input
                        type="checkbox"
                        :checked="selectedSubmissions.length === submissionItems.length && submissionItems.length > 0"
                        @click="toggleSelectAll"
                        class="w-4 h-4 cursor-pointer"
                    />
                </td>
                <td>ID</td>
                <td>Applicant Information</td>
                <td>Nationality</td>
                <td>Date Created</td>
                <td>Date Updated</td>
                <td>Submission Status</td>
                <td>Application Status</td>
            </tr>
            <tr v-if="isLoadingSubmissions">
                <td colspan="8">Loading...</td>
            </tr>
            <tr v-else-if="errorLoadingSubmissions">
                <td colspan="8">Error loading results.</td>
            </tr>
            <tr v-else-if="!submissionItems.length">
                <td colspan="8">No items to display.</td>
            </tr>
            <tr
                v-else
                v-for="(submission, index) in submissionItems"
                :key="index"
                class="transition hover:bg-deep-blue/[.03]"
                :class="[submission.status === 'Rejected' ? '!text-error' : '!text-dark-deep-blue']"
            >
                <td @click.stop>
                    <input
                        type="checkbox"
                        :checked="isSubmissionSelected(submission)"
                        @click="toggleSubmissionSelection(submission)"
                        class="w-4 h-4 cursor-pointer"
                    />
                </td>
                <td @click="doGoToSubmission(submission.id)" class="cursor-pointer">
                    <div class="flex">
                        <AppropriatenessScorePillComponent :score="submission.appropriateness_score" class="mr-3" />
                        {{ submission.id }}
                    </div>
                </td>
                <td @click="doGoToSubmission(submission.id)" class="cursor-pointer">
                    {{ submission.title }}
                    <strong>{{ submission.name }}</strong>
                    <span title="Account Number" class="ml-1" v-if="submission.custom_account_number">
                        #{{ submission.custom_account_number }}
                    </span>
                    <div class="mt-1 opacity-60 text-[14px]">
                        {{ submission.date_of_birth }}
                        <span class="mx-1" v-if="submission.date_of_birth && submission.email">&#x2022;</span>
                        {{ submission.email }}
                    </div>
                </td>
                <td @click="doGoToSubmission(submission.id)" class="cursor-pointer">
                    {{ submission.nationality | country }}
                </td>
                <td @click="doGoToSubmission(submission.id)" class="cursor-pointer">
                    {{ submission.created_at }}
                </td>
                <td @click="doGoToSubmission(submission.id)" class="cursor-pointer">
                    {{ submission.updated_at }}
                </td>
                <td @click="doGoToSubmission(submission.id)" class="cursor-pointer">
                    <StatusBadgeComponent v-if="!submission.submitted_at" status="In Progress" />
                    <StatusBadgeComponent
                        v-else
                        status="Submitted"
                        :title="'Submitted on: ' + submission.submitted_at"
                    />
                </td>
                <td @click="doGoToSubmission(submission.id)" class="cursor-pointer">
                    <StatusBadgeComponent v-if="submission.status" :status="submission.status" />
                </td>
            </tr>
        </table>

        <div class="flex md:hidden">
            <div class="w-full">
                <div v-if="isLoadingSubmissions" class="flex justify-center mt-4">
                    <div class="spinner"></div>
                </div>
                <div v-else-if="errorLoadingSubmissions" class="flex justify-center mt-4">
                    <div>Error loading results.</div>
                </div>
                <div v-else-if="!submissionItems.length" class="flex justify-center mt-4">
                    <div>No items to display.</div>
                </div>
                <div v-else class="flex flex-col mt-4">
                    <div
                        class="flex justify-between items-center p-2 bg-infill-white border-b-2 border-deep-blue/[.07]">
                        <div class="font-bold">Applicant Information</div>
                        <div class="font-bold">Status</div>
                    </div>
                    <div
                        v-for="(submission, index) in submissionItems"
                        :key="index"
                        class="transition cursor-pointer hover:bg-deep-blue/[.03] border-b border-deep-blue/[.1]"
                        :class="[submission.status === 'Rejected' ? '!text-error' : '!text-dark-deep-blue']"
                        @click="doGoToSubmission(submission.id)"
                    >
                        <div class="flex justify-between items-center py-2">
                            <div class="flex items center">
                                <div>
                                    <div class="flex items center">
                                        <div class="font-bold">{{ submission.title }}</div>
                                        <div class="ml-1">{{ submission.name }}</div>
                                    </div>
                                    <div class="opacity-60 text-[14px]">
                                        <div class="flex">
                                            <AppropriatenessScorePillComponent :score="submission.appropriateness_score"
                                                                               class="mr-3" />
                                            <div class="ml-1">#{{ submission.custom_account_number }}</div>
                                        </div>
                                        <div>DOB: {{ submission.date_of_birth }}</div>
                                        <div>{{ submission.email }}</div>
                                        <div>{{ submission.nationality | country }}</div>
                                    </div>
                                </div>
                            </div>
                            <div class="flex flex-col gap-y-2 items-end">
                                <StatusBadgeComponent v-if="!submission.submitted_at" status="In Progress" />
                                <StatusBadgeComponent
                                    v-else
                                    status="Submitted"
                                    :title="'Submitted on: ' + submission.submitted_at"
                                />
                                <StatusBadgeComponent v-if="submission.status" :status="submission.status" />
                                <div class="opacity-60 text-[14px]">Created: {{ submission.created_at }}</div>
                                <div class="opacity-60 text-[14px]">Updated: {{ submission.updated_at }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import StatusBadgeComponent from "@adminComponents/misc/StatusBadgeComponent";
import InputComponent from "@adminComponents/inputs/InputComponent";
import SearchComponent from "@adminComponents/inputs/SearchComponent";
import DropdownComponent from "@adminComponents/inputs/DropdownComponent";
import AppropriatenessScorePillComponent from "@adminComponents/misc/AppropriatenessScorePillComponent";
import ButtonComponent from "@adminComponents/buttons/ButtonComponent";
import moment from "moment";

export default {
    name: "AdminDashboardPage",
    components: {
        StatusBadgeComponent,
        InputComponent,
        SearchComponent,
        DropdownComponent,
        AppropriatenessScorePillComponent,
        ButtonComponent,
    },
    computed: {
        ...mapGetters(["global", "submissions"]),
        applicationStatuses() {
            let excludeFromApplicationStatuses = [...this.submissionStatuses]; // Copy by value, not reference
            excludeFromApplicationStatuses.push("Reject"); // Also remove "Reject"
            excludeFromApplicationStatuses.push("Second Approval");
            return Object.keys(this.global.collections.submission_statuses).filter((status) => {
                // Convert submission_statuses to an array and filter the results
                return !excludeFromApplicationStatuses.includes(status); // Remove Submission Statuses
            });
        },
    },
    data: function() {
        return {
            isLoadingSubmissions: true,
            errorLoadingSubmissions: false,
            submissionItems: [],
            submissionStatuses: ["In Progress", "Submitted"],
            searchKeyword: "",
            selectedSubmissionStatusFilter: "",
            selectedApplicationStatusFilter: "",
            isLoadingCsvExport: false,
            isLoadingInProgressCsvExport: false,
            selectedSubmissions: [],
            showDeleteModal: false,
        };
    },
    methods: {
        ...mapActions(["doGetSubmissions", "doSubmissionSearch", "doBulkDeleteSubmissions", "doGenerateAllSubmissionsCsvExportUrl", "doGenerateAllInProgressSubmissionsCsvExportUrl"]),

        isSubmissionSelected(submission) {
            return this.selectedSubmissions.some(selected => selected.id === submission.id);
        },

        toggleSubmissionSelection(submission) {
            if (this.isSubmissionSelected(submission)) {
                this.selectedSubmissions = this.selectedSubmissions.filter(selected => selected.id !== submission.id);
            } else {
                this.selectedSubmissions.push(submission);
            }
        },

        toggleSelectAll() {
            if (this.selectedSubmissions.length === this.submissionItems.length) {
                this.selectedSubmissions = [];
            } else {
                this.selectedSubmissions = [...this.submissionItems];
            }
        },

        showDeleteConfirmation() {
            this.showDeleteModal = true;
        },

        handleBulkDelete() {
            if (this.selectedSubmissions.length > 0) {
                let payload = {
                    user_ids: this.selectedSubmissions.map(submission => submission.id),
                };

                this.doBulkDeleteSubmissions(payload).then(
                    (response) => {
                        this.selectedSubmissions = [];
                        this.showDeleteModal = false;
                        this.handleSearchSubmissions();
                    },
                    (error) => {
                        console.log(error);
                    },
                );
            }
        },
        doGoToSubmission(submissionId) {
            this.$router.push({ name: "admin-user-submission", params: { user: submissionId } });
        },
        handleSearchSubmissions() {
            this.buildSearchQueryUrl();
            let payload = {
                keyword: this.searchKeyword,
            };

            if (this.selectedSubmissionStatusFilter) payload.submission_status = this.selectedSubmissionStatusFilter;
            if (this.selectedApplicationStatusFilter) payload.status = this.selectedApplicationStatusFilter;

            this.isLoadingSubmissions = true;
            this.doSubmissionSearch(payload).then(
                (response) => {
                    this.isLoadingSubmissions = false;
                    this.parseSubmissionsForDataTable(response);
                },
                (error) => {
                    this.errorLoadingSubmissions = true;
                },
            );
        },
        parseSubmissionsForDataTable(submissionsResponse = []) {
            let parsedSubmissions = submissionsResponse.map((submission) => {
                return {
                    id: submission.id,
                    email: submission.email,
                    title: submission.personal_details != undefined ? submission.personal_details.title : "",
                    name:
                        submission.personal_details != undefined
                            ? submission.personal_details.first_name + " " + submission.personal_details.last_name
                            : "",
                    date_of_birth:
                        submission.personal_details != undefined
                            ? moment(submission.personal_details.date_of_birth).format("DD/MM/YYYY")
                            : "",
                    nationality:
                        submission.personal_details != undefined ? submission.personal_details.nationality : "",
                    created_at: moment(submission.created_at)
                        .utc(submission.created_at)
                        .local()
                        .format("DD - MM - YYYY"),
                    updated_at: moment(submission.updated_at)
                        .utc(submission.updated_at)
                        .local()
                        .format("DD - MM - YYYY"),
                    submitted_at:
                        submission.submission != undefined && submission.submission.submitted_at
                            ? moment(submission.submission.submitted_at)
                                .utc(submission.submission.submitted_at)
                                .local()
                                .format("DD-MM-YYYY hh:mma")
                            : "",
                    status: submission.submission != undefined ? submission.submission.status : "",
                    appropriateness_score:
                        submission.submission != undefined ? submission.submission.appropriateness_score : null,
                    custom_account_number: submission.custom_account_number ?? "",
                };
            });
            this.submissionItems = parsedSubmissions;
        },
        buildSearchQueryUrl() {
            let queryParams = {};

            // Build out the query
            if (this.searchKeyword) queryParams.search = this.searchKeyword;
            if (this.selectedSubmissionStatusFilter)
                queryParams.submission_status = this.selectedSubmissionStatusFilter;
            if (this.selectedApplicationStatusFilter)
                queryParams.application_status = this.selectedApplicationStatusFilter;

            this.$router.replace({ query: queryParams });
        },
        doDownloadSubmittedApplicationsCsv() {
            this.isLoadingCsvExport = true;
            this.doGenerateAllSubmissionsCsvExportUrl().then((response) => {
                let downloadUrl = response.file_url;
                window.open(downloadUrl, "_blank");
                this.isLoadingCsvExport = false;
            });
        },
        doDownloadInProgressApplicationsCsv() {
            this.isLoadingInProgressCsvExport = true;
            this.doGenerateAllInProgressSubmissionsCsvExportUrl().then((response) => {
                let downloadUrl = response.file_url;
                window.open(downloadUrl, "_blank");
                this.isLoadingInProgressCsvExport = false;
            });
        },
    },
    mounted() {
        this.isLoadingSubmissions = true;
        this.doGetSubmissions().then(
            (response) => {
                this.parseSubmissionsForDataTable(response);
                this.isLoadingSubmissions = false;
            },
            (error) => {
                this.errorLoadingSubmissions = true;
            },
        );

        // Set the value of the filters if there are applicable query params in the URL
        this.searchKeyword = this.$route.query.search;
        this.selectedSubmissionStatusFilter = this.$route.query.submission_status;
        this.selectedApplicationStatusFilter = this.$route.query.application_status;
    },
};
</script>
